import React, { useState, useEffect, useMemo } from 'react';
import { DailyRecord, DashboardDailyMetrics, ScheduledOccurrence, ExerciseProgram, ProgramDay, ProgramDayExercise, Exercise, WeeklyRoutineTask } from '../types';
import { getDateDetails, formatGregorianDate, getLocalDateString } from '../lib/dateUtils';
import { 
  Calendar, Droplets, Flame, BookOpen, Clock, CheckCircle2, ChevronLeft, 
  ChevronRight, Activity, Moon, Plus, Minus, Scale, 
  Check, Trash2, GraduationCap, Newspaper, Sparkles,
  CalendarCheck, ArrowUpRight, Zap, CheckCircle, ListTodo,
  Smile, Dumbbell, ShieldCheck, HeartPulse, Globe, Sun
} from 'lucide-react';
import { getNewsState, subscribeToNewsData, syncNewsDataFromCloud, NewsState } from '../lib/newsStorage';
import { useLanguage, formatNumberByLang } from '../lib/i18n';
import { loadAppState, saveAppState, subscribeToAppSettings } from '../lib/storage';
import { getDashboardMetricsForDate, toggleOccurrenceStatus, subscribeToPlannerChanges, isInitialPlannerSyncComplete } from '../lib/plannerClient';
import { formatMinutesDuration, getTaskDurationMinutes, calculateDailyMetrics } from '../lib/scoringEngine';
import { apiGet, apiPut } from '../lib/api';
import VoiceInputButton from './VoiceInputButton';
import TodayRehabPlanCard from './TodayRehabPlanCard';
import { TodayLanguageCard } from './TodayLanguageCard';

interface DashboardProps {
  currentDate: string;
  onSetDate: (dateStr: string) => void;
  record: DailyRecord;
  onUpdateRecord: (updated: DailyRecord) => void;
  onNavigateTab: (tabId: string) => void;
  isDarkMode: boolean;
  onToggleDarkMode: () => void;
  themeAccent?: string;
}

export default function Dashboard({ 
  currentDate, 
  onSetDate, 
  record, 
  onUpdateRecord, 
  onNavigateTab,
  isDarkMode,
  onToggleDarkMode,
  themeAccent = 'indigo'
}: DashboardProps) {

  const { lang, t } = useLanguage();

  // -------------------------------------------------------------
  // D1 PLANNER STATE (SINGLE SOURCE OF TRUTH)
  // -------------------------------------------------------------
  const [plannerMetrics, setPlannerMetrics] = useState<DashboardDailyMetrics | null>(null);
  const [loadingPlanner, setLoadingPlanner] = useState<boolean>(true);
  const [plannerError, setPlannerError] = useState<boolean>(false);

  const loadPlannerData = React.useCallback(async (dateStr: string, force = false) => {
    setLoadingPlanner(true);
    setPlannerError(false);
    try {
      const data = await getDashboardMetricsForDate(dateStr, force);
      if (data) {
        setPlannerMetrics(data);
      }
    } catch (err) {
      console.warn("[Dashboard] Error loading planner metrics:", err);
      setPlannerError(true);
    } finally {
      if (isInitialPlannerSyncComplete()) {
        setLoadingPlanner(false);
      }
    }
  }, []);

  useEffect(() => {
    loadPlannerData(currentDate);

    const unsubscribe = subscribeToPlannerChanges(() => {
      // Re-evaluate loading state when sync completes
      setLoadingPlanner(true);
      loadPlannerData(currentDate, true);
    });

    return () => {
      unsubscribe();
    };
  }, [currentDate, loadPlannerData]);

  const handleToggleOccurrence = async (occ: ScheduledOccurrence) => {
    const nextStatus: 'pending' | 'completed' = occ.status === 'completed' ? 'pending' : 'completed';
    
    // Optimistic UI update in component state
    if (plannerMetrics) {
      const updatedOccs: ScheduledOccurrence[] = plannerMetrics.occurrences.map(o => 
        o.id === occ.id ? { ...o, status: nextStatus, completedAt: nextStatus === 'completed' ? new Date().toISOString() : null } : o
      );
      const newMetrics = calculateDailyMetrics(updatedOccs, currentDate);
      setPlannerMetrics(newMetrics);
    }

    // Persist to Cloudflare D1
    await toggleOccurrenceStatus(currentDate, occ.id, nextStatus);

    // Keep daily record flags aligned for shift & SFI if linked
    const cat = (occ.categorySnapshot || occ.linkedEntityTypeSnapshot || '').toLowerCase();
    if (cat.includes('work')) {
      onUpdateRecord({ ...record, wentToWork: nextStatus === 'completed' });
    } else if (cat.includes('sfi')) {
      onUpdateRecord({ ...record, attendedSfi: nextStatus === 'completed' });
    }
  };

  // -------------------------------------------------------------
  // NOW & NEXT LOGIC
  // -------------------------------------------------------------
  const todayStr = useMemo(() => getLocalDateString(), []);
  const isToday = currentDate === todayStr;

  const occurrences = useMemo(() => {
    return plannerMetrics?.occurrences || [];
  }, [plannerMetrics]);

  const activeOccurrences = useMemo(() => {
    return occurrences.filter(o => o.status !== 'cancelled' && o.status !== 'skipped');
  }, [occurrences]);

  const completedOccurrencesCount = useMemo(() => {
    return activeOccurrences.filter(o => o.status === 'completed').length;
  }, [activeOccurrences]);

  const totalOccurrencesCount = activeOccurrences.length;

  const { currentActiveOcc, nextUpcomingOcc } = useMemo(() => {
    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();

    let active: ScheduledOccurrence | null = null;
    const uncompleted: ScheduledOccurrence[] = [];

    for (const occ of activeOccurrences) {
      const startStr = occ.startTimeSnapshot || '08:00';
      const [sh, sm] = startStr.split(':').map(Number);
      const startM = (sh || 0) * 60 + (sm || 0);

      let endM = startM + 45;
      if (occ.endTimeSnapshot) {
        const [eh, em] = occ.endTimeSnapshot.split(':').map(Number);
        let calcEnd = (eh || 0) * 60 + (em || 0);
        if (calcEnd < startM) calcEnd += 24 * 60;
        endM = calcEnd;
      }

      if (isToday && nowMinutes >= startM && nowMinutes < endM) {
        active = occ;
      }

      if (occ.status !== 'completed') {
        uncompleted.push(occ);
      }
    }

    if (!isToday) {
      return { currentActiveOcc: null, nextUpcomingOcc: uncompleted[0] || null };
    }

    // Find next upcoming uncompleted occurrence after now
    const upcomingAfterNow = uncompleted.find(o => {
      const [sh, sm] = (o.startTimeSnapshot || '08:00').split(':').map(Number);
      return (sh * 60 + sm) >= nowMinutes;
    });

    const next = upcomingAfterNow || uncompleted[0] || null;

    return { currentActiveOcc: active, nextUpcomingOcc: next };
  }, [activeOccurrences, isToday]);

  // -------------------------------------------------------------
  // REHAB & EXERCISE PROGRAM INTEGRATION
  // -------------------------------------------------------------
  const [weeklyRoutine, setWeeklyRoutine] = useState<WeeklyRoutineTask[]>(() => {
    try {
      return loadAppState().settings.weeklyRoutine || [];
    } catch {
      return [];
    }
  });

  const [exercisePrograms, setExercisePrograms] = useState<ExerciseProgram[]>(() => {
    try {
      return loadAppState().settings.exercisePrograms || [];
    } catch {
      return [];
    }
  });

  const [activeProgramId, setActiveProgramId] = useState<string | undefined>(() => {
    try {
      return loadAppState().settings.activeProgramId;
    } catch {
      return undefined;
    }
  });

  const [rehabExercises, setRehabExercises] = useState<Exercise[]>(() => {
    try {
      return loadAppState().settings.rehabExercises || [];
    } catch {
      return [];
    }
  });

  const [selectedDayOverride, setSelectedDayOverride] = useState<ProgramDay | null>(null);

  // Reset selected day override when currentDate changes
  useEffect(() => {
    setSelectedDayOverride(null);
  }, [currentDate]);

  // Subscribe to app settings updates
  useEffect(() => {
    const unsub = subscribeToAppSettings((settings) => {
      if (settings.exercisePrograms) setExercisePrograms(settings.exercisePrograms);
      if (settings.activeProgramId) setActiveProgramId(settings.activeProgramId);
      if (settings.rehabExercises) setRehabExercises(settings.rehabExercises);
      if (settings.weeklyRoutine) setWeeklyRoutine(settings.weeklyRoutine);
    });
    return unsub;
  }, []);

  // Subscribe to planner changes to keep weeklyRoutine refreshed
  useEffect(() => {
    const unsub = subscribeToPlannerChanges(() => {
      try {
        const fresh = loadAppState().settings.weeklyRoutine;
        if (fresh) setWeeklyRoutine(fresh);
      } catch {}
    });
    return unsub;
  }, []);

  // Fetch programs from API on mount to ensure fresh synchronization
  useEffect(() => {
    let isMounted = true;
    apiGet<ExerciseProgram[]>('/api/rehab/programs')
      .then(res => {
        if (isMounted && res.data && Array.isArray(res.data) && res.data.length > 0) {
          setExercisePrograms(res.data);
          const appState = loadAppState();
          appState.settings.exercisePrograms = res.data;
          saveAppState(appState, false);
        }
      })
      .catch(err => {
        console.warn("[Dashboard] Could not fetch rehab programs from API:", err);
      });
    return () => { isMounted = false; };
  }, []);

  // Determine today's exercise plan based strictly on weekly schedule or planner occurrences
  const { scheduledRehabProgram, scheduledRehabDay, scheduledRehabOccurrence, isExerciseDayScheduled } = useMemo(() => {
    // 1. Check if activeOccurrences on currentDate has an explicit rehab/physio occurrence
    const rehabOcc = activeOccurrences.find(occ => {
      const cat = (occ.categorySnapshot || occ.linkedEntityTypeSnapshot || '').toLowerCase();
      const title = (occ.titleSnapshot || '').toLowerCase();
      return !!occ.programIdSnapshot ||
             cat === 'rehab' || cat === 'physio' ||
             title.includes('فیزیوتراپی') || title.includes('برنامه تمرین') || title.includes('ورزش اصلاحی');
    });

    // 2. Check weeklyRoutine for currentDate's day of week
    const [year, month, dayNum] = currentDate.split('-').map(Number);
    const dayOfWeek = new Date(year, month - 1, dayNum).getDay(); // 0 = Sun, 1 = Mon, ..., 6 = Sat
    const weeklyRehabTask = weeklyRoutine.find(t => {
      if (Number(t.dayOfWeek) !== dayOfWeek) return false;
      const cat = (t.category || '').toLowerCase();
      const title = (t.title || '').toLowerCase();
      return !!t.programId ||
             cat === 'rehab' || cat === 'physio' ||
             title.includes('فیزیوتراپی') || title.includes('برنامه تمرین') || title.includes('ورزش اصلاحی');
    });

    const isScheduled = Boolean(rehabOcc || weeklyRehabTask);

    // CRITICAL: If today has NO scheduled exercise activity in the weekly schedule or occurrences,
    // do NOT display the program card!
    if (!isScheduled) {
      return {
        scheduledRehabProgram: null,
        scheduledRehabDay: null,
        scheduledRehabOccurrence: null,
        isExerciseDayScheduled: false
      };
    }

    let prog: ExerciseProgram | null = null;
    let day: ProgramDay | null = null;

    if (rehabOcc?.programIdSnapshot) {
      prog = exercisePrograms.find(p => p.id === rehabOcc.programIdSnapshot) || null;
      if (prog && rehabOcc.programDayIdSnapshot) {
        day = prog.days?.find(d => d.id === rehabOcc.programDayIdSnapshot) || null;
      }
    }

    if (!day && weeklyRehabTask?.programId) {
      if (!prog) prog = exercisePrograms.find(p => p.id === weeklyRehabTask.programId) || null;
      if (prog && weeklyRehabTask.programDayId) {
        day = prog.days?.find(d => d.id === weeklyRehabTask.programDayId) || null;
      }
    }

    // Only if a task specifically matches an active program title
    if (!prog) {
      const searchTitle = (rehabOcc?.titleSnapshot || weeklyRehabTask?.title || '').toLowerCase();
      if (searchTitle) {
        prog = exercisePrograms.find(p => searchTitle.includes(p.title.toLowerCase()) || p.title.toLowerCase().includes(searchTitle)) || null;
      }
    }

    // If still no program, only check activeProgramId if the scheduled task explicitly belongs to the rehab category
    if (!prog && (weeklyRehabTask?.category === 'rehab' || rehabOcc?.categorySnapshot === 'rehab')) {
      prog = exercisePrograms.find(p => p.id === activeProgramId) ||
             exercisePrograms.find(p => p.isActive) || null;
    }

    if (prog && !day && prog.days && prog.days.length > 0) {
      const searchTitle = (rehabOcc?.titleSnapshot || weeklyRehabTask?.title || '').toLowerCase();
      if (searchTitle) {
        day = prog.days.find(d => searchTitle.includes(d.title.toLowerCase()) || d.title.toLowerCase().includes(searchTitle)) || null;
      }
      if (!day) {
        // If a program is scheduled but no specific day was designated, select day 1
        day = prog.days[0];
      }
    }

    // Must have both a program and a day with exercises to be scheduled
    const isValidExerciseDay = Boolean(prog && day && day.exercises && day.exercises.length > 0);

    return {
      scheduledRehabProgram: isValidExerciseDay ? prog : null,
      scheduledRehabDay: isValidExerciseDay ? day : null,
      scheduledRehabOccurrence: rehabOcc || null,
      isExerciseDayScheduled: isValidExerciseDay
    };
  }, [activeOccurrences, currentDate, exercisePrograms, activeProgramId, weeklyRoutine]);

  const currentRehabDay = selectedDayOverride || scheduledRehabDay;

  // Toggle single exercise completion with full synchronization
  const handleToggleRehabExerciseCompletion = async (exercise: ProgramDayExercise, index: number) => {
    if (!scheduledRehabProgram || !currentRehabDay) return;

    const targetEx = currentRehabDay.exercises[index];
    const currentCompleted = Boolean(
      targetEx.isCompleted ||
      (targetEx.id && record.rehabCompletedIds?.includes(targetEx.id)) ||
      (targetEx.exerciseId && record.rehabCompletedIds?.includes(targetEx.exerciseId))
    );
    const newCompleted = !currentCompleted;

    const updatedDayExercises = [...(currentRehabDay.exercises || [])];
    updatedDayExercises[index] = {
      ...targetEx,
      isCompleted: newCompleted
    };

    const updatedDay = { ...currentRehabDay, exercises: updatedDayExercises };
    const updatedDays = scheduledRehabProgram.days.map(d => d.id === currentRehabDay.id ? updatedDay : d);
    const updatedProgram = { ...scheduledRehabProgram, days: updatedDays };

    // 1. Update state in Dashboard
    setExercisePrograms(prev => prev.map(p => p.id === updatedProgram.id ? updatedProgram : p));
    if (selectedDayOverride && selectedDayOverride.id === currentRehabDay.id) {
      setSelectedDayOverride(updatedDay);
    }

    // 2. Save in appState
    const appState = loadAppState();
    appState.settings.exercisePrograms = (appState.settings.exercisePrograms || []).map(p =>
      p.id === updatedProgram.id ? updatedProgram : p
    );
    saveAppState(appState, true);

    // 3. Persist to cloud D1
    apiPut(`/api/rehab/programs/${updatedProgram.id}`, {
      title: updatedProgram.title,
      description: updatedProgram.description,
      days: updatedProgram.days,
      category: updatedProgram.category,
      difficulty: updatedProgram.difficulty,
      is_active: updatedProgram.isActive ? 1 : 0
    }).catch(err => console.warn("[Dashboard] Failed to persist program update:", err));

    // 4. Synchronize record.rehabCompletedIds
    const exKey = targetEx.id || targetEx.exerciseId;
    let nextCompleted = [...(record.rehabCompletedIds || [])];
    if (exKey) {
      if (newCompleted) {
        if (!nextCompleted.includes(exKey)) nextCompleted.push(exKey);
      } else {
        nextCompleted = nextCompleted.filter(id => id !== exKey && id !== targetEx.id && id !== targetEx.exerciseId);
      }
      onUpdateRecord({ ...record, rehabCompletedIds: nextCompleted });
    }

    // 5. If all exercises for this day are now completed, mark the occurrence as completed!
    const allDone = updatedDayExercises.length > 0 && updatedDayExercises.every(ex => {
      if (ex.isCompleted) return true;
      const k = ex.id || ex.exerciseId;
      return k && nextCompleted.includes(k);
    });

    if (scheduledRehabOccurrence) {
      if (allDone && scheduledRehabOccurrence.status !== 'completed') {
        handleToggleOccurrence(scheduledRehabOccurrence);
      }
    }
  };

  // -------------------------------------------------------------
  // TODO SCRATCHPAD STATE
  // -------------------------------------------------------------
  const [newTodoText, setNewTodoText] = useState('');
  const todos = record.todos || [];

  const handleAddTodo = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTodoText.trim()) return;

    const newTodo = {
      id: `todo_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      text: newTodoText.trim(),
      completed: false
    };

    onUpdateRecord({
      ...record,
      todos: [...todos, newTodo]
    });
    setNewTodoText('');
  };

  const handleToggleTodo = (todoId: string) => {
    onUpdateRecord({
      ...record,
      todos: todos.map(t => t.id === todoId ? { ...t, completed: !t.completed } : t)
    });
  };

  const handleDeleteTodo = (todoId: string) => {
    onUpdateRecord({
      ...record,
      todos: todos.filter(t => t.id !== todoId)
    });
  };

  // -------------------------------------------------------------
  // NEWS STATE
  // -------------------------------------------------------------
  const [liveNewsState, setLiveNewsState] = useState<NewsState>(() => getNewsState());

  useEffect(() => {
    syncNewsDataFromCloud().then(st => {
      if (st) setLiveNewsState(st);
    }).catch(err => console.warn(err));

    const unsubscribe = subscribeToNewsData((st) => {
      setLiveNewsState(st);
    });
    return () => unsubscribe();
  }, []);

  const headlines = useMemo(() => {
    const list: string[] = [];
    if (liveNewsState.lastBriefingMarkdown) {
      const lines = liveNewsState.lastBriefingMarkdown.split('\n');
      lines.forEach((line: string) => {
        if (line.startsWith('#### 📌') || line.startsWith('### 📌') || line.startsWith('####')) {
          const clean = line.replace(/^[#\s📌*]+/, '').trim();
          if (clean && !clean.includes('بولتن') && !clean.includes('جمع‌بندی')) {
            list.push(clean);
          }
        }
      });
    }
    return list.slice(0, 2);
  }, [liveNewsState]);

  // -------------------------------------------------------------
  // HEALTH & WEIGHT COMPUTATION ENGINE
  // -------------------------------------------------------------
  const sleepQualityBadge = useMemo(() => {
    const hours = record.sleepHours || 0;
    if (hours === 0) return null;
    if (hours >= 7 && hours <= 9) {
      return { label: t("sleepOptimal", "Optimal Sleep 😴"), color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' };
    }
    if (hours > 9) {
      return { label: t("sleepLong", "Long Sleep 💤"), color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300' };
    }
    if (hours >= 5 && hours < 7) {
      return { label: t("sleepModerate", "Moderate Sleep 🌙"), color: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300' };
    }
    return { label: t("sleepLow", "Low Sleep ⚠️"), color: 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300' };
  }, [record.sleepHours, t]);

  const appState = useMemo(() => loadAppState(), []);
  const settings: Record<string, any> = appState.settings || {};

  const currentWeight = record.weight || settings.currentWeight || settings.weightKg;
  const targetWeight = settings.weightGoal;
  const targetCalories = settings.targetCalories || 2000;
  const height = settings.height || settings.heightCm || 175;
  const age = settings.age || 30;
  const gender = settings.gender || 'male';
  const act = settings.activityLevel || 'moderate';

  const weightAnalysis = useMemo(() => {
    if (!currentWeight || !targetWeight) {
      return { hasData: false };
    }

    const diff = Math.abs(currentWeight - targetWeight);
    if (diff < 0.1) {
      return {
        hasData: true,
        isReached: true,
        currentWeight,
        targetWeight,
        remaining: 0,
        progressPct: 100
      };
    }

    const initialWeight = settings.startWeight || (currentWeight > targetWeight ? currentWeight + 5 : currentWeight - 5);
    const totalGoalDiff = Math.abs(initialWeight - targetWeight) || 10;
    const progressPct = Math.max(0, Math.min(100, Math.round(((totalGoalDiff - diff) / totalGoalDiff) * 100)));

    const bmr = (10 * currentWeight) + (6.25 * height) - (5 * age) + (gender === 'male' ? 5 : -161);
    const mults: Record<string, number> = { sedentary: 1.2, light: 1.375, moderate: 1.55, active: 1.725, very_active: 1.9, high: 1.725, athlete: 1.9 };
    const tdee = Math.round(bmr * (mults[act] || 1.55));

    const isLoss = currentWeight > targetWeight;
    const isGain = currentWeight < targetWeight;

    let delta = 0;
    if (isLoss) {
      delta = tdee - targetCalories;
    } else if (isGain) {
      delta = targetCalories - tdee;
    }

    if (delta <= 0) {
      return {
        hasData: true,
        hasForecast: false,
        isReached: false,
        currentWeight,
        targetWeight,
        remaining: Number(diff.toFixed(1)),
        progressPct
      };
    }

    const kgPerWeek = (delta * 7) / 7700;
    const weeks = Math.max(1, Math.round(diff / kgPerWeek));
    const forecastDate = new Date();
    forecastDate.setDate(forecastDate.getDate() + weeks * 7);

    const forecastDateStr = formatGregorianDate(forecastDate, lang, 'medium');

    return {
      hasData: true,
      hasForecast: true,
      isReached: false,
      currentWeight,
      targetWeight,
      remaining: Number(diff.toFixed(1)),
      progressPct,
      weeks,
      forecastDateStr
    };
  }, [currentWeight, targetWeight, targetCalories, height, age, gender, act, settings.startWeight, lang]);

  // -------------------------------------------------------------
  // DATE NAVIGATION & 3-CALENDAR HEADER
  // -------------------------------------------------------------
  const dateDetails = getDateDetails(currentDate, lang);

  const handlePrevDay = () => {
    const [y, m, d] = currentDate.split('-').map(Number);
    const prevDate = new Date(y, m - 1, d - 1);
    const yStr = prevDate.getFullYear();
    const mStr = String(prevDate.getMonth() + 1).padStart(2, '0');
    const dStr = String(prevDate.getDate()).padStart(2, '0');
    onSetDate(`${yStr}-${mStr}-${dStr}`);
  };

  const handleNextDay = () => {
    const [y, m, d] = currentDate.split('-').map(Number);
    const nextDate = new Date(y, m - 1, d + 1);
    const yStr = nextDate.getFullYear();
    const mStr = String(nextDate.getMonth() + 1).padStart(2, '0');
    const dStr = String(nextDate.getDate()).padStart(2, '0');
    onSetDate(`${yStr}-${mStr}-${dStr}`);
  };

  const handleBackToToday = () => {
    onSetDate(todayStr);
  };

  // Health calculations
  const totalCalories = useMemo(() => {
    return record.foods?.reduce((sum, f) => sum + (f.calories || 0), 0) || 0;
  }, [record.foods]);

  // -------------------------------------------------------------
  // HEALTH ADJUSTERS
  // -------------------------------------------------------------
  const adjustWater = (amount: number) => {
    onUpdateRecord({
      ...record,
      waterIntake: Math.max(0, (record.waterIntake || 0) + amount)
    });
  };

  const calculateSleepDurationHours = (start?: string, end?: string): number => {
    if (!start || !end) return 0;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    if (isNaN(sh) || isNaN(sm) || isNaN(eh) || isNaN(em)) return 0;
    let startM = sh * 60 + sm;
    let endM = eh * 60 + em;
    if (endM < startM) endM += 24 * 60;
    const diffHours = (endM - startM) / 60;
    return Math.max(0, Math.min(24, Math.round(diffHours * 10) / 10));
  };

  const handleSleepTimeChange = (startTime: string, wakeTime: string) => {
    const calcHours = calculateSleepDurationHours(startTime, wakeTime);
    onUpdateRecord({
      ...record,
      sleepTime: startTime,
      wakeTime: wakeTime,
      sleepHours: calcHours > 0 ? calcHours : record.sleepHours || 0
    });
  };

  const adjustSleep = (amount: number) => {
    onUpdateRecord({
      ...record,
      sleepHours: Math.max(0, Math.min(24, Number(((record.sleepHours || 0) + amount).toFixed(1))))
    });
  };

  const getCategoryIcon = (category?: string, linkedType?: string) => {
    const cat = (category || linkedType || '').toLowerCase();
    if (cat.includes('rehab') || cat.includes('physio')) return <ShieldCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />;
    if (cat.includes('fat-loss') || cat.includes('workout') || cat.includes('exercise')) return <Flame className="w-4 h-4 text-rose-600 dark:text-rose-400" />;
    if (cat.includes('swedish') || cat.includes('sfi') || cat.includes('study')) return <GraduationCap className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />;
    if (cat.includes('work')) return <Activity className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />;
    return <Clock className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />;
  };

  return (
    <div className="space-y-5 animate-fadeIn pb-12">
      
      {/* 1. TOP COMMAND HEADER (3 DATE SYSTEMS IN HEADER ONLY) */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 md:p-5 border border-gray-200 dark:border-gray-700 shadow-xs space-y-3">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          
          {/* Weekday & Date Badges */}
          <div className="space-y-2">
            <div className="flex items-center gap-2.5 flex-wrap">
              <span className="p-2.5 rounded-xl bg-indigo-50 dark:bg-indigo-950/80 text-indigo-600 dark:text-indigo-400">
                <Calendar className="w-5 h-5" />
              </span>

              {/* Single Weekday */}
              <h1 className="text-lg md:text-2xl font-black tracking-tight text-gray-900 dark:text-white capitalize">
                {dateDetails.weekdayName}
              </h1>

              {isToday ? (
                <span className="px-3 py-0.5 rounded-full text-xs font-black bg-emerald-100 text-emerald-800 dark:bg-emerald-950/90 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800 shadow-2xs">
                  {t("schedulesTodayBadge", "Today")}
                </span>
              ) : (
                <span className="px-3 py-0.5 rounded-full text-xs font-black bg-amber-100 text-amber-800 dark:bg-amber-950/90 dark:text-amber-300 border border-amber-300 dark:border-amber-800 shadow-2xs">
                  {t("viewingDate", "Viewing another date")}
                </span>
              )}
            </div>

            {/* 3 Date Systems (Miladi / Jalali / Hijri) */}
            <div className="flex flex-wrap items-center gap-2 text-xs md:text-sm font-bold pt-0.5">
              {/* Gregorian / Miladi */}
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/60 font-mono">
                <Globe className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                <span>{dateDetails.gregorianDateOnly}</span>
              </span>

              {/* Jalali / Persian */}
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-50 dark:bg-amber-950/50 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800/60 font-mono">
                <Sun className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                <span>{dateDetails.jalaliDateOnly}</span>
              </span>

              {/* Arabic / Hijri */}
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/50 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/60 font-mono">
                <Moon className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <span>{dateDetails.hijriDateOnly}</span>
              </span>
            </div>
          </div>

          {/* Navigation Controls */}
          <div className="flex items-center gap-2 self-end md:self-start">
            <button
              type="button"
              onClick={handlePrevDay}
              className="p-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-100 cursor-pointer transition-colors shadow-2xs"
              title={t("prevDay", "Previous Day")}
            >
              <ChevronRight className="w-4 h-4 rtl:rotate-0 ltr:rotate-180" />
            </button>

            {!isToday && (
              <button
                type="button"
                onClick={handleBackToToday}
                className="px-3.5 py-2 text-xs rounded-xl border border-indigo-300 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-950/80 text-indigo-700 dark:text-indigo-300 font-black hover:bg-indigo-100 dark:hover:bg-indigo-900 cursor-pointer shadow-2xs"
              >
                {t("schedulesBackToToday", "🕒 Back to Today")}
              </button>
            )}

            <button
              type="button"
              onClick={handleNextDay}
              className="p-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-100 cursor-pointer transition-colors shadow-2xs"
              title={t("nextDay", "Next Day")}
            >
              <ChevronLeft className="w-4 h-4 rtl:rotate-0 ltr:rotate-180" />
            </button>
          </div>

        </div>
      </div>

      {/* 2. NOW / NEXT SPOTLIGHT BANNER */}
      {(currentActiveOcc || nextUpcomingOcc) && (() => {
        const targetOcc = currentActiveOcc || nextUpcomingOcc;
        const isCompleted = targetOcc?.status === 'completed';
        const isNow = !!currentActiveOcc;
        
        return (
          <div className={`rounded-2xl p-4 md:p-5 border shadow-sm transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
            isNow
              ? 'bg-indigo-50/50 dark:bg-indigo-950/20 border-indigo-100 dark:border-indigo-900/60'
              : 'bg-amber-50/40 dark:bg-amber-950/10 border-amber-100 dark:border-amber-900/30'
          }`}>
            <div className="space-y-1.5 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {isNow ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-black bg-rose-500 text-white animate-pulse">
                    <Zap className="w-3 h-3 fill-white" />
                    <span>{t("schedulesNowBadge", "Active Now | NOW")}</span>
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-black bg-amber-500 text-slate-950">
                    <Clock className="w-3 h-3" />
                    <span>{t("schedulesNextBadge", "Next Event | NEXT")}</span>
                  </span>
                )}
                
                <span className={`text-xs font-mono font-bold px-2 py-0.5 rounded-md ${
                  isNow 
                    ? 'bg-indigo-100/80 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300' 
                    : 'bg-amber-100/80 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                }`} dir="ltr">
                  {targetOcc?.startTimeSnapshot}
                  {targetOcc?.endTimeSnapshot ? ` - ${targetOcc?.endTimeSnapshot}` : ''}
                </span>

                {isCompleted && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.2 rounded bg-emerald-100 dark:bg-emerald-950 text-emerald-800 dark:text-emerald-300 text-[10px] font-bold border border-emerald-200 dark:border-emerald-900/40">
                    <Check className="w-2.5 h-2.5 stroke-[3]" />
                    <span>{t("schedulesCompletedStatus", "Completed")}</span>
                  </span>
                )}
              </div>

              <h3 className={`text-base md:text-lg font-extrabold truncate ${
                isCompleted 
                  ? 'line-through text-gray-400 dark:text-gray-500' 
                  : 'text-gray-900 dark:text-white'
              }`}>
                {targetOcc?.titleSnapshot}
              </h3>
              
              {targetOcc?.notes && (
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate font-medium">
                  {targetOcc?.notes}
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={() => handleToggleOccurrence(targetOcc!)}
              className={`px-4 py-2 text-xs font-black rounded-xl transition-all shadow-2xs flex items-center justify-center gap-2 cursor-pointer shrink-0 active:scale-95 ${
                isCompleted
                  ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                  : isNow
                  ? 'bg-indigo-600 hover:bg-indigo-500 text-white'
                  : 'bg-amber-600 hover:bg-amber-500 text-white'
              }`}
            >
              <Check className="w-4 h-4 stroke-[3]" />
              <span>{isCompleted ? t("schedulesCompletedStatus", "Completed") : t("markCompleted", "Mark Completed")}</span>
            </button>
          </div>
        );
      })()}

      {/* 2.5. TODAY'S REHAB EXERCISE PLAN & ACTIVITIES (ONLY IF SCHEDULED FOR TODAY) */}
      {(isExerciseDayScheduled && scheduledRehabProgram && currentRehabDay && currentRehabDay.exercises && currentRehabDay.exercises.length > 0) ? (
        <TodayRehabPlanCard
          program={scheduledRehabProgram}
          day={currentRehabDay}
          allDays={scheduledRehabProgram?.days || []}
          currentDate={currentDate}
          onSelectDay={(day) => setSelectedDayOverride(day)}
          record={record}
          onUpdateRecord={onUpdateRecord}
          onToggleExerciseCompletion={handleToggleRehabExerciseCompletion}
          onNavigateTab={onNavigateTab}
          availableExercises={rehabExercises}
          matchedOccurrence={scheduledRehabOccurrence}
          onToggleOccurrence={handleToggleOccurrence}
          defaultExpanded={true}
        />
      ) : null}

      {/* 2.6. TODAY'S LANGUAGE LEARNING PLAN & SKILLS CARD (ONLY IF SCHEDULED FOR DATE) */}
      <TodayLanguageCard onNavigate={onNavigateTab} currentDate={currentDate} />

      {/* 3. DAILY TASKS COMMAND CENTER */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 md:p-5 border border-gray-200 dark:border-gray-700 shadow-xs space-y-4">
        
        {/* Header & Progress Bar */}
        <div className="flex items-center justify-between gap-3 pb-3 border-b border-gray-100 dark:border-gray-700 flex-wrap">
          <div className="flex items-center gap-2.5">
            <span className="p-2 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400">
              <Zap className="w-5 h-5" />
            </span>
            <div>
              <h2 className="text-sm md:text-base font-extrabold text-gray-900 dark:text-white flex items-center gap-2">
                <span>{t("dashTodaysPlanTitle", "Daily Plan & Tasks")}</span>
                {totalOccurrencesCount > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300 text-[11px] font-bold">
                    {formatNumberByLang(completedOccurrencesCount, lang)} / {formatNumberByLang(totalOccurrencesCount, lang)}
                  </span>
                )}
              </h2>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                {plannerError
                  ? t("dashErrorLoadingPlan", "Failed to load today's plan")
                  : (loadingPlanner || !isInitialPlannerSyncComplete())
                  ? t("dashLoadingPlan", "Loading today's plan...")
                  : totalOccurrencesCount > 0
                  ? t("dashTasksProgressSummary", "{{completed}} of {{total}} activities completed", {
                      completed: formatNumberByLang(completedOccurrencesCount, lang),
                      total: formatNumberByLang(totalOccurrencesCount, lang)
                    })
                  : t("dashNoTasksToday", "No routines scheduled for this day")}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => onNavigateTab('weekly-routine')}
            className="px-3 py-1.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100 text-xs font-extrabold flex items-center gap-1 cursor-pointer transition-colors shadow-2xs"
          >
            <span>{t("schedulesTabManage", "⚙️ Manage Weekly Schedule")}</span>
            <ArrowUpRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Loading / Empty State / Tasks List */}
        {plannerError ? (
          <div className="p-6 text-center flex flex-col items-center gap-3 bg-red-50/50 dark:bg-red-900/10 rounded-xl border border-red-100 dark:border-red-900/30">
            <p className="text-xs text-red-600 dark:text-red-400 font-bold">{t("dashErrorLoadingPlan", "Failed to load today's plan")}</p>
            <button
              onClick={() => loadPlannerData(currentDate, true)}
              className="px-3 py-1.5 bg-red-100 hover:bg-red-200 dark:bg-red-900/50 dark:hover:bg-red-900/70 text-red-700 dark:text-red-300 rounded-lg text-xs font-bold transition-colors cursor-pointer"
            >
              {t("retry", "Retry")}
            </button>
          </div>
        ) : (loadingPlanner || !isInitialPlannerSyncComplete()) ? (
          <div className="p-8 flex flex-col justify-center items-center gap-3 text-xs text-gray-400 font-bold border border-gray-100 dark:border-gray-800 rounded-xl bg-gray-50/50 dark:bg-gray-800/20">
            <svg className="animate-spin h-6 w-6 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>{t("dashLoadingPlan", "Loading today's plan...")}</span>
          </div>
        ) : activeOccurrences.length === 0 ? (
          <div className="p-5 text-center bg-gray-50 dark:bg-gray-900/40 rounded-xl border border-dashed border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3 text-right">
              <Smile className="w-6 h-6 text-indigo-500 shrink-0" />
              <div>
                <h3 className="text-xs font-extrabold text-gray-800 dark:text-gray-200">
                  {t("dashRestDayTitle", "Rest & Recovery Day")}
                </h3>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  {t("dashRestDayDesc", "No mandatory tasks scheduled for this day.")}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => onNavigateTab('weekly-routine')}
              className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-extrabold cursor-pointer shadow-2xs shrink-0"
            >
              {t("schedulesAddActivity", "+ Add Activity")}
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {activeOccurrences.map((occ) => {
              const isCompleted = occ.status === 'completed';
              const isCurrent = currentActiveOcc?.id === occ.id;
              const isNext = nextUpcomingOcc?.id === occ.id && !isCurrent && !isCompleted;
              const dur = getTaskDurationMinutes(occ.startTimeSnapshot, occ.endTimeSnapshot, occ.categorySnapshot, occ.titleSnapshot);

              return (
                <div
                  key={occ.id}
                  onClick={() => handleToggleOccurrence(occ)}
                  className={`p-3 md:p-3.5 rounded-xl border transition-all cursor-pointer flex items-center justify-between gap-3 ${
                    isCurrent
                      ? 'bg-indigo-50/70 dark:bg-indigo-950/40 border-indigo-400 dark:border-indigo-700 shadow-xs ring-1 ring-indigo-300 dark:ring-indigo-800'
                      : isCompleted
                      ? 'bg-emerald-50/40 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-800/80 opacity-90'
                      : isNext
                      ? 'bg-amber-50/40 dark:bg-amber-950/20 border-amber-300 dark:border-amber-800 shadow-2xs'
                      : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-700 shadow-2xs'
                  }`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {/* Checkbox */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleOccurrence(occ);
                      }}
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-all cursor-pointer ${
                        isCompleted
                          ? 'bg-emerald-600 border-emerald-600 text-white shadow-2xs'
                          : 'border-gray-400 dark:border-gray-500 hover:border-indigo-600 bg-white dark:bg-gray-800'
                      }`}
                    >
                      {isCompleted && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                    </button>

                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs font-mono font-bold text-gray-700 dark:text-gray-300" dir="ltr">
                          🕒 {occ.startTimeSnapshot}{occ.endTimeSnapshot ? ` - ${occ.endTimeSnapshot}` : ''}
                        </span>

                        {dur > 0 && (
                          <span className="text-[10px] text-gray-400 font-mono">
                            ({formatMinutesDuration(dur, lang)})
                          </span>
                        )}

                        {isCurrent && (
                          <span className="text-[10px] px-2 py-0.2 rounded-md bg-indigo-600 text-white font-extrabold animate-pulse">
                            {t("schedulesCurrentActiveTask", "Active Now")}
                          </span>
                        )}

                        {isNext && (
                          <span className="text-[10px] px-2 py-0.2 rounded-md bg-amber-500 text-white font-extrabold">
                            {t("dashNextCardTitle", "Next")}
                          </span>
                        )}

                        {occ.isRequiredSnapshot ? (
                          <span className="text-[10px] px-1.5 py-0.2 rounded-md bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300 font-bold border border-indigo-200 dark:border-indigo-900">
                            {t("dashTaskRequired", "Required")}
                          </span>
                        ) : (
                          <span className="text-[10px] px-1.5 py-0.2 rounded-md bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 font-bold border border-gray-200 dark:border-gray-700">
                            {t("dashTaskOptional", "Optional")}
                          </span>
                        )}
                      </div>

                      <h4 className={`text-xs md:text-sm font-extrabold truncate ${
                        isCompleted ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'
                      }`}>
                        {occ.titleSnapshot}
                      </h4>
                    </div>
                  </div>

                  <div className="shrink-0 flex items-center gap-2">
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-extrabold ${
                      isCompleted 
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' 
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                    }`}>
                      {isCompleted ? t("schedulesCompletedStatus", "Completed") : t("schedulesPendingStatus", "Not Completed")}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* All done congratulation */}
        {totalOccurrencesCount > 0 && completedOccurrencesCount === totalOccurrencesCount && (
          <div className="p-3.5 rounded-xl bg-emerald-100 dark:bg-emerald-950/60 border border-emerald-300 dark:border-emerald-800 text-center space-y-0.5 animate-fadeIn">
            <p className="text-xs md:text-sm font-extrabold text-emerald-900 dark:text-emerald-200">
              🎉 {t("dashAllTasksDoneCongrats", "All scheduled activities for today are completed!")}
            </p>
          </div>
        )}

      </div>

      {/* 4. COMPACT HEALTH TRACKERS (Water, Calories, Sleep, Weight) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        
        {/* Hydration Card */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xs space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
              <Droplets className="w-4 h-4 text-blue-500" />
              <span>{t("water", "Water Intake")}</span>
            </span>
            <span className="text-xs font-mono font-extrabold text-gray-900 dark:text-white">
              {formatNumberByLang(record.waterIntake || 0, lang)} / 2000 ml
            </span>
          </div>

          <div className="w-full bg-gray-100 dark:bg-gray-700 h-2 rounded-full overflow-hidden">
            <div 
              className="bg-blue-500 h-full rounded-full transition-all duration-300"
              style={{ width: `${Math.min(100, ((record.waterIntake || 0) / 2000) * 100)}%` }}
            />
          </div>

          <div className="flex items-center gap-1.5 pt-0.5">
            <button
              type="button"
              onClick={() => adjustWater(-250)}
              className="flex-1 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl text-xs font-bold cursor-pointer transition-colors"
            >
              -250 ml
            </button>
            <button
              type="button"
              onClick={() => adjustWater(250)}
              className="flex-1 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold cursor-pointer transition-colors shadow-2xs"
            >
              +250 ml
            </button>
          </div>
        </div>

        {/* Calories Card */}
        <div 
          onClick={() => onNavigateTab('nutrition')}
          className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xs space-y-3 cursor-pointer hover:border-indigo-300 dark:hover:border-indigo-700 transition-all group"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
              <Flame className="w-4 h-4 text-orange-500" />
              <span>{t("calories", "Calories")}</span>
            </span>
            <span className="text-xs font-mono font-extrabold text-gray-900 dark:text-white">
              {formatNumberByLang(totalCalories, lang)} kcal
            </span>
          </div>

          <div className="w-full bg-gray-100 dark:bg-gray-700 h-2 rounded-full overflow-hidden">
            <div 
              className="bg-gradient-to-r from-orange-400 to-rose-500 h-full rounded-full transition-all duration-300"
              style={{ width: `${Math.min(100, (totalCalories / (settings.targetCalories || 2000)) * 100)}%` }}
            />
          </div>

          <div className="flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400 pt-0.5">
            <span>{t("nutritionTitle", "Nutrition & Calories")} ({t("capLabel", "Cap")}: {formatNumberByLang(settings.targetCalories || 2000, lang)})</span>
            <ArrowUpRight className="w-4 h-4 text-indigo-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </div>
        </div>

        {/* Sleep Tracker Card */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xs space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-1">
            <span className="text-xs font-bold text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
              <Moon className="w-4 h-4 text-indigo-400" />
              <span>{t("sleep", "Sleep Hours")}</span>
            </span>
            <div className="flex items-center gap-1.5">
              {sleepQualityBadge && (
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${sleepQualityBadge.color}`}>
                  {sleepQualityBadge.label}
                </span>
              )}
              <span className="text-xs font-mono font-extrabold text-gray-900 dark:text-white">
                {formatNumberByLang(record.sleepHours || 0, lang)} h
              </span>
            </div>
          </div>

          {/* Sleep Start & Wake-Up Time Inputs */}
          <div className="grid grid-cols-2 gap-2 pt-0.5">
            <div className="space-y-1">
              <label className="text-[10px] font-extrabold text-gray-500 dark:text-gray-400 block">
                {t("sleepStartTime", "Bedtime")}
              </label>
              <input
                type="time"
                value={record.sleepTime || '23:30'}
                onChange={(e) => handleSleepTimeChange(e.target.value, record.wakeTime || '07:30')}
                className="w-full px-2 py-1.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-mono font-bold text-gray-900 dark:text-white text-center focus:outline-none focus:border-indigo-500"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-extrabold text-gray-500 dark:text-gray-400 block">
                {t("sleepWakeTime", "Wake Time")}
              </label>
              <input
                type="time"
                value={record.wakeTime || '07:30'}
                onChange={(e) => handleSleepTimeChange(record.sleepTime || '23:30', e.target.value)}
                className="w-full px-2 py-1.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-mono font-bold text-gray-900 dark:text-white text-center focus:outline-none focus:border-indigo-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-4 gap-1 pt-0.5">
            <button
              type="button"
              onClick={() => adjustSleep(-1)}
              className="py-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg text-[11px] font-bold cursor-pointer transition-colors"
              title="-1h"
            >
              -1h
            </button>
            <button
              type="button"
              onClick={() => adjustSleep(-0.5)}
              className="py-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg text-[11px] font-bold cursor-pointer transition-colors"
              title="-0.5h"
            >
              -0.5h
            </button>
            <button
              type="button"
              onClick={() => adjustSleep(0.5)}
              className="py-1 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 rounded-lg text-[11px] font-bold cursor-pointer transition-colors border border-indigo-200 dark:border-indigo-800"
              title="+0.5h"
            >
              +0.5h
            </button>
            <button
              type="button"
              onClick={() => adjustSleep(1)}
              className="py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-[11px] font-bold cursor-pointer transition-colors shadow-2xs"
              title="+1h"
            >
              +1h
            </button>
          </div>
        </div>

        {/* Weight Tracker & Goal Forecast Card */}
        <div 
          onClick={() => onNavigateTab('weight-tracker')}
          className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xs space-y-3 cursor-pointer hover:border-emerald-400 dark:hover:border-emerald-600 transition-all group"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
              <Scale className="w-4 h-4 text-emerald-500" />
              <span>{t("weight", "Weight & Goal Tracker")}</span>
            </span>
            <span className="text-xs font-mono font-extrabold text-emerald-700 dark:text-emerald-300">
              {currentWeight ? `${formatNumberByLang(currentWeight, lang)} kg` : '--'}
            </span>
          </div>

          {weightAnalysis.hasData ? (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-gray-500 dark:text-gray-400 truncate max-w-[170px]">
                  {weightAnalysis.isReached 
                    ? t("weightTargetReached", "🎉 Target weight reached") 
                    : t("weightTargetProgress", "Goal: {{target}} kg ({{remaining}} kg left)", {
                        target: formatNumberByLang(weightAnalysis.targetWeight!, lang),
                        remaining: formatNumberByLang(weightAnalysis.remaining!, lang)
                      })}
                </span>
                <span className="text-xs font-bold text-gray-700 dark:text-gray-300">
                  {formatNumberByLang(Math.round(weightAnalysis.progressPct), lang)}%
                </span>
              </div>
              <div className="w-full bg-gray-100 dark:bg-gray-700 h-2 rounded-full overflow-hidden">
                <div 
                  className="bg-emerald-500 h-full rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(100, weightAnalysis.progressPct)}%` }}
                />
              </div>
              {weightAnalysis.hasForecast && weightAnalysis.forecastDateStr && (
                <div className="text-[10px] font-bold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 p-1.5 rounded-lg border border-emerald-200/60 dark:border-emerald-900/40 flex items-center justify-between">
                  <span>{t("weightTargetEstDate", "Estimated target date:")}</span>
                  <span className="font-mono">{weightAnalysis.forecastDateStr}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400 pt-0.5">
              <span>{t("viewProfileMetrics", "View Weight Chart & Tracking")}</span>
              <ArrowUpRight className="w-4 h-4 text-emerald-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </div>
          )}
        </div>

      </div>

      {/* 5. QUICK MODULE ACCESS GRID */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 md:p-6 border border-gray-200 dark:border-gray-700 shadow-xs space-y-3">
        <h3 className="text-xs font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          {t("quickAccessModules", "Quick Access Modules")}
        </h3>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2.5">
          
          <button
            type="button"
            onClick={() => onNavigateTab('language-learning')}
            className="p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-emerald-400 bg-gray-50 dark:bg-gray-900/60 hover:bg-emerald-50/50 dark:hover:bg-emerald-950/30 text-right cursor-pointer transition-all space-y-1.5"
          >
            <Globe className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            <div className="text-xs font-extrabold text-gray-900 dark:text-white">{t("languageLearningTitle", "Language Learning")}</div>
          </button>

          <button
            type="button"
            onClick={() => onNavigateTab('rehab')}
            className="p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-emerald-400 bg-gray-50 dark:bg-gray-900/60 hover:bg-emerald-50/50 dark:hover:bg-emerald-950/30 text-right cursor-pointer transition-all space-y-1.5"
          >
            <ShieldCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            <div className="text-xs font-extrabold text-gray-900 dark:text-white">{t("rehabTitle", "Physiotherapy & Spine Rehab")}</div>
          </button>

          <button
            type="button"
            onClick={() => onNavigateTab('nutrition')}
            className="p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-orange-400 bg-gray-50 dark:bg-gray-900/60 hover:bg-orange-50/50 dark:hover:bg-orange-950/30 text-right cursor-pointer transition-all space-y-1.5"
          >
            <Activity className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            <div className="text-xs font-extrabold text-gray-900 dark:text-white">{t("nutritionTitle", "Nutrition & Calories")}</div>
          </button>

          <button
            type="button"
            onClick={() => onNavigateTab('swedish')}
            className="p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-yellow-400 bg-gray-50 dark:bg-gray-900/60 hover:bg-yellow-50/50 dark:hover:bg-yellow-950/30 text-right cursor-pointer transition-all space-y-1.5"
          >
            <GraduationCap className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            <div className="text-xs font-extrabold text-gray-900 dark:text-white">{t("swedishTitle", "Swedish Education")}</div>
          </button>

          <button
            type="button"
            onClick={() => onNavigateTab('schedules')}
            className="p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-indigo-400 bg-gray-50 dark:bg-gray-900/60 hover:bg-indigo-50/50 dark:hover:bg-indigo-950/30 text-right cursor-pointer transition-all space-y-1.5"
          >
            <Calendar className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            <div className="text-xs font-extrabold text-gray-900 dark:text-white">{t("schedulesTitle", "Weekly Planner & Daily Routines")}</div>
          </button>

          <button
            type="button"
            onClick={() => onNavigateTab('notes')}
            className="p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-violet-400 bg-gray-50 dark:bg-gray-900/60 hover:bg-violet-50/50 dark:hover:bg-violet-950/30 text-right cursor-pointer transition-all space-y-1.5"
          >
            <ListTodo className="w-5 h-5 text-violet-600 dark:text-violet-400" />
            <div className="text-xs font-extrabold text-gray-900 dark:text-white">{t("notesTitle", "Notes & Journal")}</div>
          </button>

        </div>
      </div>

      {/* 6. DAILY SCRATCHPAD / QUICK TODOS */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 md:p-6 border border-gray-200 dark:border-gray-700 shadow-xs space-y-4">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-extrabold text-gray-900 dark:text-white flex items-center gap-2">
            <ListTodo className="w-4 h-4 text-indigo-500" />
            <span>{t("dailyScratchpad", "Daily Notes & Quick Tasks")}</span>
          </h3>

          <span className="text-xs text-gray-400 font-bold">
            {todos.filter(t => t.completed).length} / {todos.length} {t("doneUnit", "done")}
          </span>
        </div>

        {/* Add Todo Form */}
        <form onSubmit={handleAddTodo} className="flex gap-2">
          <input
            type="text"
            value={newTodoText}
            onChange={(e) => setNewTodoText(e.target.value)}
            placeholder={t("addTodoPlaceholder", "Add new task...")}
            className="flex-1 px-3.5 py-2 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:border-indigo-500 text-gray-900 dark:text-white"
          />
          <VoiceInputButton
            currentValue={newTodoText}
            onUpdate={(val) => setNewTodoText(val)}
          />
          <button
            type="submit"
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-extrabold cursor-pointer transition-colors"
          >
            {t("add", "Add")}
          </button>
        </form>

        {/* Todo List */}
        {todos.length > 0 && (
          <div className="space-y-1.5 pt-2">
            {todos.map((todo) => (
              <div 
                key={todo.id}
                className="flex items-center justify-between p-2.5 rounded-xl border border-gray-100 dark:border-gray-800/80 bg-gray-50/50 dark:bg-gray-900/30"
              >
                <label className="flex items-center gap-2.5 cursor-pointer flex-1 min-w-0">
                  <input
                    type="checkbox"
                    checked={todo.completed}
                    onChange={() => handleToggleTodo(todo.id)}
                    className="w-4 h-4 accent-indigo-600 rounded cursor-pointer"
                  />
                  <span className={`text-xs ${todo.completed ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-800 dark:text-gray-200'}`}>
                    {todo.text}
                  </span>
                </label>

                <button
                  type="button"
                  onClick={() => handleDeleteTodo(todo.id)}
                  className="p-1 text-gray-400 hover:text-rose-600 rounded-lg transition-colors cursor-pointer"
                  title={t("delete", "Delete")}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 7. LIVE NEWS HEADLINES (OPTIONAL COMPACT PREVIEW) */}
      {headlines.length > 0 && (
        <div className="bg-gray-50 dark:bg-gray-900/40 rounded-2xl p-4 border border-gray-200 dark:border-gray-800 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Newspaper className="w-4 h-4 text-indigo-500 shrink-0" />
            <p className="text-xs text-gray-700 dark:text-gray-300 truncate font-bold">
              {headlines[0]}
            </p>
          </div>

          <button
            type="button"
            onClick={() => onNavigateTab('news')}
            className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline font-extrabold flex items-center gap-0.5 cursor-pointer shrink-0"
          >
            <span>{t("newsBulletin", "View News Bulletin")}</span>
            <ArrowUpRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

    </div>
  );
}
